import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
    Mail, Clock, CheckSquare, GitBranch, Sparkles, Tag, Bell, Flag,
    Check, Circle, AlertCircle, Pause, X, Loader2, ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from './ui/button';

const API = process.env.REACT_APP_BACKEND_URL || '';

const STEP_ICONS = {
    email: Mail, wait: Clock, task: CheckSquare, branch: GitBranch,
    ai_branch: Sparkles, tag_update: Tag, notification: Bell, end: Flag,
};

export default function CampaignProgressTracker({ contactId }) {
    const { getAuthHeader } = useAuth();
    const [enrollments, setEnrollments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState({});

    useEffect(() => {
        if (!contactId) return;
        const load = async () => {
            try {
                const res = await axios.get(`${API}/api/campaigns/contact/${contactId}/progress`, { headers: getAuthHeader() });
                setEnrollments(res.data || []);
            } catch { /* ignore */ }
            finally { setLoading(false); }
        };
        load();
    }, [contactId, getAuthHeader]);

    const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

    const handlePause = async (enrollmentId) => {
        try {
            await axios.put(`${API}/api/campaigns/enrollments/${enrollmentId}/pause`, {}, { headers: getAuthHeader() });
            setEnrollments(prev => prev.map(e =>
                e.enrollment_id === enrollmentId ? { ...e, status: 'paused' } : e
            ));
        } catch { /* ignore */ }
    };

    const handleResume = async (enrollmentId) => {
        try {
            await axios.put(`${API}/api/campaigns/enrollments/${enrollmentId}/resume`, {}, { headers: getAuthHeader() });
            setEnrollments(prev => prev.map(e =>
                e.enrollment_id === enrollmentId ? { ...e, status: 'active' } : e
            ));
        } catch { /* ignore */ }
    };

    // v2.16.39 BUG-038: Remove a contact from a sequence. Backend already has
    // DELETE /api/campaigns/enrollments/{id} — just wasn't surfaced in the UI.
    const handleRemove = async (enrollmentId, campaignName) => {
        const ok = window.confirm(
            `Remove this contact from "${campaignName || 'this sequence'}"? ` +
            `They will stop receiving further emails and their journey will end. This cannot be undone.`
        );
        if (!ok) return;
        try {
            await axios.delete(`${API}/api/campaigns/enrollments/${enrollmentId}`, { headers: getAuthHeader() });
            setEnrollments(prev => prev.filter(e => e.enrollment_id !== enrollmentId));
        } catch (err) {
            // Keep the UI honest — fall through rather than swallow silently.
            alert('Failed to remove from sequence. ' + (err.response?.data?.detail || ''));
        }
    };

    if (loading) return null;
    if (enrollments.length === 0) return null;

    return (
        <div className="space-y-3">
            {enrollments.map(enrollment => {
                const steps = enrollment.campaign_steps || [];
                const currentStepId = enrollment.current_step_id;
                const history = enrollment.step_history || [];
                const completedStepIds = new Set(history.filter(h => h.exited_at).map(h => h.step_id));
                const isExpanded = expanded[enrollment.enrollment_id];
                const isActive = enrollment.status === 'active';
                const isPaused = enrollment.status === 'paused';
                const isCompleted = enrollment.status === 'completed';

                // Calculate progress
                const completedCount = completedStepIds.size;
                const totalSteps = steps.length;
                const progressPct = totalSteps > 0 ? Math.round((completedCount / totalSteps) * 100) : 0;

                return (
                    <div key={enrollment.enrollment_id} className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                        {/* Header */}
                        <button
                            onClick={() => toggleExpand(enrollment.enrollment_id)}
                            className="w-full px-4 py-3 flex items-center justify-between hover:bg-stone-50 transition-colors text-left"
                        >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                    isActive ? 'bg-emerald-500 animate-pulse' :
                                    isPaused ? 'bg-amber-500' :
                                    isCompleted ? 'bg-blue-500' : 'bg-stone-300'
                                }`} />
                                <div className="flex-1 min-w-0">
                                    <span className="text-sm font-medium text-stone-900 truncate block">{enrollment.campaign_name}</span>
                                    <span className="text-xs text-stone-400">
                                        Step {enrollment.current_step_order + 1} of {totalSteps}
                                        {enrollment.current_step_label && ` — ${enrollment.current_step_label}`}
                                    </span>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-medium text-stone-500">{progressPct}%</span>
                                {isExpanded ? <ChevronUp className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />}
                            </div>
                        </button>

                        {/* Progress Bar */}
                        <div className="px-4 pb-3">
                            <div className="flex items-center gap-1">
                                {steps.map((step, idx) => {
                                    const isStepCompleted = completedStepIds.has(step.step_id);
                                    const isCurrent = step.step_id === currentStepId;
                                    const StepIcon = STEP_ICONS[step.step_type] || Circle;

                                    return (
                                        <React.Fragment key={step.step_id}>
                                            {/* Step dot */}
                                            <div className="flex flex-col items-center" title={step.label || step.step_type}>
                                                <div className={`w-7 h-7 rounded-full flex items-center justify-center transition-all ${
                                                    isStepCompleted ? 'bg-emerald-500 text-white' :
                                                    isCurrent ? 'bg-blue-500 text-white ring-2 ring-blue-200' :
                                                    'bg-stone-100 text-stone-400'
                                                }`}>
                                                    {isStepCompleted ? <Check className="w-3.5 h-3.5" /> :
                                                     <StepIcon className="w-3.5 h-3.5" />}
                                                </div>
                                                <span className={`text-[9px] mt-0.5 max-w-[50px] truncate text-center ${
                                                    isCurrent ? 'text-blue-600 font-medium' : 'text-stone-400'
                                                }`}>{step.label || step.step_type}</span>
                                            </div>
                                            {/* Connector line */}
                                            {idx < steps.length - 1 && (
                                                <div className={`flex-1 h-0.5 mt-[-12px] ${
                                                    isStepCompleted ? 'bg-emerald-400' : 'bg-stone-200'
                                                }`} />
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Expanded Details */}
                        {isExpanded && (
                            <div className="px-4 pb-4 border-t border-stone-100 pt-3">
                                {/* Step History */}
                                <div className="space-y-1.5 mb-3">
                                    {history.map((h, idx) => (
                                        <div key={idx} className="flex items-center gap-2 text-xs">
                                            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                                                h.outcome === 'sent' || h.outcome === 'completed' ? 'bg-emerald-500' :
                                                h.outcome === 'failed' ? 'bg-red-500' :
                                                h.outcome === 'branched' ? 'bg-blue-500' :
                                                'bg-stone-300'
                                            }`} />
                                            <span className="text-stone-600 font-medium">{h.step_label || h.step_type}</span>
                                            {h.outcome && <span className="text-stone-400 capitalize">({h.outcome})</span>}
                                            <span className="text-stone-300 ml-auto">{h.entered_at ? new Date(h.entered_at).toLocaleDateString() : ''}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* Actions */}
                                <div className="flex items-center gap-2">
                                    {isActive && (
                                        <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => handlePause(enrollment.enrollment_id)}>
                                            <Pause className="w-3 h-3 mr-1" /> Pause
                                        </Button>
                                    )}
                                    {isPaused && (
                                        <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => handleResume(enrollment.enrollment_id)}>
                                            <Circle className="w-3 h-3 mr-1" /> Resume
                                        </Button>
                                    )}
                                    {/* v2.16.39 BUG-038 */}
                                    {(isActive || isPaused) && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="text-xs h-7 text-red-600 hover:text-red-700 hover:border-red-300"
                                            onClick={() => handleRemove(enrollment.enrollment_id, enrollment.campaign_name)}
                                            title="Remove this contact from the sequence (ends their journey)"
                                        >
                                            <X className="w-3 h-3 mr-1" /> Remove
                                        </Button>
                                    )}
                                    <span className="text-xs text-stone-400 ml-auto">
                                        Enrolled {new Date(enrollment.enrolled_at).toLocaleDateString()}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
